import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem, Switch,
  FormControlLabel, Tooltip, alpha, Avatar, Divider, Skeleton
} from '@mui/material';
import {
  Add, Edit, Delete, Search, People, Close,
  Phone, Email, CalendarToday, Badge, LocationOn, Notes,
  VisibilityOff, Visibility, OpenInNew
} from '@mui/icons-material';
import { contractorsAPI, usersAPI, providersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { formatPhone, fullName, US_STATES } from '../../utils/formatters';
import { useTableSort } from '../../components/useTableSort';

const empty = {
  firstName: '', lastName: '', middleInitial: '', last4SSN: '',
  street: '', city: '', state: '', zip: '', country: 'US',
  dateOfBirth: '', startDate: '', endDate: '',
  commissionType: 'all', phone: '', email: '', gender: '', linkedUser: '', isActive: true, notes: ''
};

const Contractors = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [detailPopup, setDetailPopup] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [c, u, p] = await Promise.all([contractorsAPI.getAll({ limit: 1000 }), usersAPI.getAll(), providersAPI.getAll({ limit: 1000 })]);
      setRows(Array.isArray(c.data) ? c.data : c.data?.data || []); setUsers(Array.isArray(u.data) ? u.data : u.data?.data || []);
      setProviders(Array.isArray(p.data) ? p.data : p.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Contractors | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  const visibleRows = showInactive ? rows : rows.filter(r => r.isActive !== false);
  const inactiveCount = rows.filter(r => r.isActive === false).length;

  const columns = useMemo(() => [
    { id: 'contractorId', label: 'ID', getValue: r => r.contractorId || '' },
    { id: 'contractor', label: 'Contractor', getValue: r => fullName(r) || '' },
    { id: 'contact', label: 'Contact', getValue: r => r.phone || '' },
    { id: 'providers', label: 'Assigned Providers', getValue: r => {
      const provs = (r.assignedProviders || []).filter(p => p.provider);
      return provs.map(p => fullName(p.provider)).join(', ') || '—';
    } },
    { id: 'commission', label: 'Commission', getValue: r => r.commissionType || 'all' },
    { id: 'startDate', label: 'Start Date', getValue: r => r.startDate || '', getDisplay: r => r.startDate ? new Date(r.startDate).toLocaleDateString() : '—' },
    { id: 'status', label: 'Status', getValue: r => r.isActive !== false ? 'Active' : 'Inactive' },
  ], []);

  const searchFiltered = useMemo(() => {
    if (!search) return visibleRows;
    return visibleRows.filter(r =>
      [r.firstName, r.lastName, r.email, r.phone, r.contractorId].some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
    );
  }, [visibleRows, search]);

  const { sortedData: filtered, renderHeader, renderFilterPopover, renderClearFilters } = useTableSort(columns, searchFiltered);

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      setForm({
        firstName: row.firstName || '', lastName: row.lastName || '', middleInitial: row.middleInitial || '',
        last4SSN: row.last4SSN || '',
        street: row.street || '', city: row.city || '', state: row.state || '', zip: row.zip || '', country: row.country || 'US',
        dateOfBirth: row.dateOfBirth ? row.dateOfBirth.slice(0, 10) : '',
        startDate: row.startDate ? row.startDate.slice(0, 10) : '',
        endDate: row.endDate ? row.endDate.slice(0, 10) : '',
        commissionType: row.commissionType || 'all', phone: row.phone || '', email: row.email || '',
        gender: row.gender || '', linkedUser: row.linkedUser?._id || row.linkedUser || '',
        isActive: row.isActive !== false, notes: row.notes || ''
      });
    } else { setEditing(null); setForm(empty); }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const d = { ...form }; if (!d.linkedUser) delete d.linkedUser;
      if (editing) await contractorsAPI.update(editing, d);
      else await contractorsAPI.create(d);
      setOpen(false); load();
      toast.success(editing ? 'Contractor updated successfully' : 'Contractor created successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async () => {
    try { await contractorsAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Contractor deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const F = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const commTypes = [
    { value: 'insurance_income', label: 'Insurance Income' },
    { value: 'patient_income', label: 'Patient Income' },
    { value: 'all', label: 'All Income' }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Contractors</Typography>
          <Typography variant="body2" color="text.secondary">Manage billing contractors and commissions</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
          sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 4px 14px rgba(139,92,246,0.3)', px: 3, fontWeight: 700 }}>
          Add Contractor
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField size="small" placeholder="Search contractors..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
            <Tooltip title={showInactive ? 'Hide inactive contractors' : `Show inactive contractors (${inactiveCount})`}>
              <IconButton onClick={() => setShowInactive(p => !p)} sx={{ color: showInactive ? '#ef4444' : '#94a3b8' }}>
                {showInactive ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
            {renderClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  {renderHeader(<TableCell align="right">Actions</TableCell>)}
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                          <People sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No contractors found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => (
                        <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#8b5cf6', 0.03) } }}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#8b5cf6', fontFamily: 'monospace' }}>{r.contractorId}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700, background: `linear-gradient(135deg, ${alpha('#8b5cf6', 0.15)}, ${alpha('#ec4899', 0.15)})`, color: '#8b5cf6' }}>
                                {(r.firstName || '?')[0]}
                              </Avatar>
                              <Box>
                                <Typography
                                  onClick={() => setDetailPopup(r)}
                                  sx={{ fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', '&:hover': { color: '#8b5cf6', textDecoration: 'underline' } }}>
                                  {fullName(r)}
                                </Typography>
                                {r.gender && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{r.gender}</Typography>}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.78rem' }}>{r.phone || '—'}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{r.email || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const assigned = providers.filter(p => (p.assignedContractors || []).some(a => (a.contractor?._id || a.contractor) === r._id));
                              const show = assigned.slice(0, 2);
                              const extra = assigned.length - 2;
                              return assigned.length ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {show.map(p => <Chip key={p._id} label={fullName(p)} size="small" sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', bgcolor: alpha('#3b82f6', 0.08), color: '#2563eb' }} />)}
                                  {extra > 0 && <Tooltip title={assigned.slice(2).map(p => fullName(p)).join(', ')}><Chip label={`+${extra}`} size="small" sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem', bgcolor: alpha('#64748b', 0.1), color: '#475569', cursor: 'pointer' }} /></Tooltip>}
                                </Box>
                              ) : <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>—</Typography>;
                            })()}
                          </TableCell>
                          <TableCell>
                            <Chip label={commTypes.find(c => c.value === r.commissionType)?.label || r.commissionType || 'All'} size="small"
                              sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#f59e0b', 0.1), color: '#d97706' }} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.82rem' }}>{r.startDate ? new Date(r.startDate).toLocaleDateString() : '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={r.isActive !== false ? 'Active' : 'Inactive'} size="small"
                              sx={{ fontWeight: 700, height: 24, bgcolor: r.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: r.isActive !== false ? '#059669' : '#dc2626' }} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#8b5cf6' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>
      {renderFilterPopover()}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', borderRadius: 2 }}>
              <People sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{editing ? 'Edit Contractor' : 'Add Contractor'}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Fill in the contractor details</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>First Name *</Typography>
              <TextField fullWidth size="small" placeholder="John" value={form.firstName} onChange={e => F('firstName', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Badge sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Last Name *</Typography>
              <TextField fullWidth size="small" placeholder="Doe" value={form.lastName} onChange={e => F('lastName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>MI</Typography>
              <TextField fullWidth size="small" placeholder="A" inputProps={{ maxLength: 1 }} value={form.middleInitial} onChange={e => F('middleInitial', e.target.value.toUpperCase())} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Last 4 SSN</Typography>
              <TextField fullWidth size="small" placeholder="1234" inputProps={{ maxLength: 4 }} value={form.last4SSN} onChange={e => F('last4SSN', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Gender</Typography>
              <TextField fullWidth size="small" select value={form.gender} onChange={e => F('gender', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Phone</Typography>
              <TextField fullWidth size="small" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => F('phone', formatPhone(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Email</Typography>
              <TextField fullWidth size="small" placeholder="email@example.com" value={form.email} onChange={e => F('email', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Street Address</Typography>
              <TextField fullWidth size="small" placeholder="123 Main St" value={form.street} onChange={e => F('street', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>City</Typography>
              <TextField fullWidth size="small" placeholder="New York" value={form.city} onChange={e => F('city', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>State</Typography>
              <TextField fullWidth size="small" select value={form.state} onChange={e => F('state', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Zip</Typography>
              <TextField fullWidth size="small" placeholder="10001" value={form.zip} onChange={e => F('zip', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Country</Typography>
              <TextField fullWidth size="small" value={form.country} onChange={e => F('country', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Commission Type</Typography>
              <TextField fullWidth size="small" select value={form.commissionType} onChange={e => F('commissionType', e.target.value)}>
                {commTypes.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Linked User</Typography>
              <TextField fullWidth size="small" select value={form.linkedUser} onChange={e => F('linkedUser', e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {users.map(u => <MenuItem key={u._id} value={u._id}>{u.firstName} {u.lastName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Status</Typography>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={e => F('isActive', e.target.checked)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' } }} />}
                label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{form.isActive ? 'Active' : 'Inactive'}</Typography>} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Date of Birth</Typography>
              <TextField fullWidth size="small" type="date" value={form.dateOfBirth} onChange={e => F('dateOfBirth', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Start Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.startDate} onChange={e => F('startDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>End Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.endDate} onChange={e => F('endDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Notes</Typography>
              <TextField fullWidth size="small" multiline rows={2} placeholder="Additional notes..." value={form.notes} onChange={e => F('notes', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, px: 4 }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Contractor?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Contractor Detail Popup */}
      <Dialog open={!!detailPopup} onClose={() => setDetailPopup(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {detailPopup && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>
                  {(detailPopup.firstName || '?')[0]}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{fullName(detailPopup)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{detailPopup.contractorId}</Typography>
                </Box>
              </Box>
              <Box>
                <Tooltip title="Edit Contractor">
                  <IconButton size="small" onClick={() => { setDetailPopup(null); handleOpen(detailPopup); }} sx={{ color: '#8b5cf6' }}>
                    <OpenInNew sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setDetailPopup(null)}><Close /></IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
              <Grid container spacing={1.5}>
                {detailPopup.email && <Grid item xs={12}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Email</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.email}</Typography></Grid>}
                {detailPopup.phone && <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Phone</Typography><Typography sx={{ fontSize: '0.85rem' }}>{formatPhone(detailPopup.phone)}</Typography></Grid>}
                {detailPopup.gender && <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Gender</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.gender}</Typography></Grid>}
                <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Status</Typography><Chip label={detailPopup.isActive !== false ? 'Active' : 'Inactive'} size="small" sx={{ fontWeight: 700, height: 22, bgcolor: detailPopup.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: detailPopup.isActive !== false ? '#059669' : '#dc2626' }} /></Grid>
                <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Commission</Typography><Typography sx={{ fontSize: '0.85rem' }}>{commTypes.find(c => c.value === detailPopup.commissionType)?.label || detailPopup.commissionType}</Typography></Grid>
                {(detailPopup.street || detailPopup.city) && <Grid item xs={12}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Address</Typography><Typography sx={{ fontSize: '0.85rem' }}>{[detailPopup.street, detailPopup.city, detailPopup.state, detailPopup.zip].filter(Boolean).join(', ')}</Typography></Grid>}
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Contractors;
